import { Star } from "lucide-react";
import type { MouseEvent, PointerEvent } from "react";

import { useUser } from "@/context/user-context";
import { cn } from "@/lib/utils";
import { useListFavorites, useStarFavorite, useUnstarFavorite } from "@/queries/favorites";
import { EntityType } from "@/queries/folder-entities/types";

interface EntityFavoriteStarProps {
  entityType: EntityType;
  entityId: string | number;
  className?: string;
  /** Icon size classes; defaults match registry grid/list. */
  iconClassName?: string;
}

/**
 * Compact favorite star for registry grid/list rows.
 * Uses the shared favorites list (no per-row stars API).
 * Always visible when starred; outline star only on parent `group` hover when not.
 */
export const EntityFavoriteStar = ({
  entityType,
  entityId,
  className,
  iconClassName = "h-3.5 w-3.5",
}: EntityFavoriteStarProps) => {
  const workspaceId = useUser().activeWorkspaceId ?? 0;
  const normalizedEntityId = String(entityId);

  const { data } = useListFavorites({
    workspaceId,
    enabled: !!workspaceId && !!normalizedEntityId,
  });
  const starFavorite = useStarFavorite();
  const unstarFavorite = useUnstarFavorite();

  const isStarred =
    data?.favorites.some(
      favorite => favorite.entity_type === entityType && favorite.entity_id === normalizedEntityId,
    ) ?? false;

  const isMutating = starFavorite.isLoading || unstarFavorite.isLoading;

  if (!workspaceId || !normalizedEntityId) {
    return null;
  }

  const stopPropagation = (e: MouseEvent | PointerEvent) => {
    e.stopPropagation();
  };

  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (isMutating) {
      return;
    }

    const payload = {
      workspaceId,
      entityType,
      entityId: normalizedEntityId,
    };

    if (isStarred) {
      unstarFavorite.mutate(payload);
    } else {
      starFavorite.mutate(payload);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onPointerDown={stopPropagation}
      onMouseDown={stopPropagation}
      disabled={isMutating}
      aria-label={isStarred ? "Remove from favorites" : "Add to favorites"}
      className={cn(
        "relative z-20 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded",
        "transition-opacity duration-150 hover:bg-gray-100 focus:outline-none",
        isStarred
          ? "opacity-100"
          : "pointer-events-none opacity-0 group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100",
        className,
      )}
    >
      <Star
        className={cn(
          iconClassName,
          isStarred ? "fill-yellow-500 text-yellow-500" : "text-gray-400 hover:text-yellow-500",
        )}
        strokeWidth={2}
      />
    </button>
  );
};

export default EntityFavoriteStar;
